import type { MenuContext, MenuScreen } from '../types.js';
import type { GameRecord } from '../../history/types.js';
import {
  createScreenShell,
  createMenuHeader,
  createCrestButton,
  el,
  confirmDialog,
} from '@bn-games/ui-shell';
import {
  relativeTime,
  createDiscardModal,
  type RelativeTimeStrings,
} from '@bn-games/history-core/ui';
import { t } from '../../i18n/I18n.js';
import { formatTime } from '../settingsRows.js';

function relativeTimeStrings(): RelativeTimeStrings {
  return {
    justNow: t('historyList.justNow'),
    minutesAgo: (count) => t('historyList.minutesAgo', { count }),
    hoursAgo: (count) => t('historyList.hoursAgo', { count }),
    daysAgo: (count) => t('historyList.daysAgo', { count }),
  };
}

function difficultyLabel(record: GameRecord): string {
  const base = t(`settings.difficulties.${record.difficulty}`);
  if (record.forfeited === true) return `${base} · ${t('historyList.forfeitedTag')}`;
  return base;
}

function summary(record: GameRecord): string {
  const time = formatTime(record.timeMs);
  const hints = t('historyList.hintsCount', { count: record.hintsUsed });
  const errors = t('historyList.errorsCount', { count: record.errorsMade });
  return `${time} · ${hints} · ${errors}`;
}

export function createHistoryListScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  function paint(root: HTMLElement, ctx: MenuContext): void {
    while (root.firstChild) root.removeChild(root.firstChild);

    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;

    body.appendChild(createMenuHeader({ title: t('history.title') }));

    const records = ctx.historyStore.list();
    const list = el('div', { class: 'history-list' });

    if (records.length === 0) {
      list.appendChild(
        el('div', {
          class: 'history-empty',
          text: t('history.empty'),
        }),
      );
    } else {
      for (const rec of records) {
        list.appendChild(createRow(rec, ctx, () => paint(root, ctx)));
      }
    }

    body.appendChild(list);

    const actions: HTMLElement[] = [
      createCrestButton({
        label: t('common.back'),
        small: true,
        onClick: () => ctx.navigate('menu'),
      }),
    ];
    if (records.length > 0) {
      actions.push(
        createCrestButton({
          label: t('historyList.clearAll'),
          small: true,
          onClick: () => {
            createDiscardModal({
              strings: {
                title: t('historyList.clearAllConfirm'),
                body: t('historyList.clearAllBody'),
                keep: t('common.cancel'),
                discard: t('historyList.clearAll'),
              },
              onDiscard: () => {
                for (const r of ctx.historyStore.list()) {
                  ctx.historyStore.delete(r.id);
                }
                paint(root, ctx);
              },
            });
          },
        }),
      );
    }
    const buttonRow = el('div', { class: 'button-row back-button-row' }, actions);
    body.appendChild(buttonRow);

    root.appendChild(frame);
  }

  function createRow(record: GameRecord, ctx: MenuContext, repaint: () => void): HTMLElement {
    const when = relativeTime(record.finishedAt, relativeTimeStrings());
    const meta = el('div', { class: 'history-row__meta' }, [
      el('div', {
        class: 'history-row__title',
        text: `${difficultyLabel(record)} — ${when}`,
      }),
      el('div', { class: 'history-row__sub', text: summary(record) }),
    ]);

    const trash = el('button', {
      class: 'history-row__icon-btn history-row__delete',
      attrs: {
        type: 'button',
        'aria-label': t('historyList.deleteAria'),
        title: t('historyList.deleteAria'),
      },
      text: '✕',
      on: {
        click: (e: Event) => {
          e.stopPropagation();
          void confirmDialog({
            title: t('historyList.deleteConfirm'),
            message: '',
            danger: true,
          }).then((ok) => {
            if (!ok) return;
            ctx.historyStore.delete(record.id);
            repaint();
          });
        },
      },
    });

    const rowActions = el('div', { class: 'history-row__actions' }, [trash]);

    return el(
      'div',
      {
        class: 'history-row',
        on: { click: () => ctx.replayGame(record) },
      },
      [meta, rowActions],
    );
  }

  return {
    mount(root, ctx) {
      paint(root, ctx);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
